import { debounce, each, random } from 'lodash'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import * as THREE from 'three'

import { Wrapper, Inner, Plug } from './styles'

const DNA_WIDTH = 428
const DNA_HEIGHT = 790

export default class DnaEffect extends Component {
  static propTypes = {
    restrictUIX: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props)

    this.wrapperRef = React.createRef()
    this.debouncedResizeHandler = debounce(this.resizeHandler, 200)

    this.nodesInitialCount = 100
    this.nodesCounter = this.nodesInitialCount
    this.nodesDistanceH = 6
    this.nodesDistanceV = 0.5
    this.nodesAngle = 5
    this.nodesMaxSize = 0.9
    this.nodesColor = 0xffffff
    this.connectorEvery = 5
    this.connectorNodesCount = 15
    this.connectorMaxSize = 0.7
    this.speedRetraction = 12
    this.speedDelta = 0
    this.dnaRotationSpeed = 0.014
    this.animPositionDistance = 3
    this.animPositionK = 1.0
    this.animOpacityK = 0.02

    this.scene = null
    this.camera = null
    this.renderer = null
    this.dna = null
    this.dnaNodeMaterial = null
    this.dnaNodeSprite = null

    this.nodesPosition = []

    this.animationId = null
  }

  componentDidMount() {
    const { restrictUIX } = this.props

    if (!restrictUIX.noWebGL) {
      this.launch()
    }
  }

  componentDidUpdate(prevProps) {
    const { restrictUIX } = this.props

    if (restrictUIX.noWebGL !== prevProps.restrictUIX.noWebGL) {
      if (restrictUIX.noWebGL) {
        this.stop()
      } else {
        this.launch()
      }
    }
  }

  componentWillUnmount() {
    this.stop()
  }

  launch = () => {
    this.initScene()
    this.animationId = requestAnimationFrame(this.animate)
    this.resizeHandler()
    window.addEventListener('resize', this.debouncedResizeHandler)
  }

  stop = () => {
    if (
      this.wrapperRef.current &&
      this.renderer.domElement.parentElement === this.wrapperRef.current
    ) {
      this.wrapperRef.current.removeChild(this.renderer.domElement)
    }

    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }

    window.removeEventListener('resize', this.debouncedResizeHandler)
  }

  initScene = () => {
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(
      45,
      DNA_WIDTH / DNA_HEIGHT,
      1,
      1000,
    )
    this.camera.position.y = 0
    this.camera.position.z = 500

    this.renderer = new THREE.WebGLRenderer({ alpha: true })
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(DNA_WIDTH, DNA_HEIGHT)

    // create dna object
    this.dna = new THREE.Object3D()
    this.dna.position.y = 30
    this.dna.position.z = 480
    this.scene.add(this.dna)

    // create node material and sprite base
    this.dnaNodeMaterial = new THREE.SpriteMaterial({
      color: this.nodesColor,
      fog: true,
    })
    this.dnaNodeSprite = new THREE.Sprite()
    this.dnaNodeSprite.position.normalize()

    this.build()

    if (this.wrapperRef.current) {
      this.wrapperRef.current.appendChild(this.renderer.domElement)
    }
  }

  build = () => {
    for (let i = 0; i <= this.nodesInitialCount; i++) {
      const row = this.createDnaRow(i)

      this.explodeNodes(row)
      this.dna.add(row)
    }
  }

  explodeNodes = row => {
    each(row.children, child => {
      // create arrays of node positions
      const nodeVector = child.position.clone()
      const nodeNewVector = new THREE.Vector3(
        random(-this.animPositionDistance, this.animPositionDistance),
        random(-this.animPositionDistance, this.animPositionDistance),
        random(-this.animPositionDistance, this.animPositionDistance),
      )

      this.nodesPosition[child.id] = [nodeVector, nodeNewVector]

      // init random position
      child.position.x = nodeVector.x + nodeNewVector.x
      child.position.y = nodeVector.y + nodeNewVector.y
      child.position.z = nodeVector.z + nodeNewVector.z

      // init opacity
      child.material.userData = child.material.opacity
      child.material.opacity = 0
    })
  }

  createDnaRow = i => {
    // create nodes row
    const row = new THREE.Object3D()

    row.position.y = -i * this.nodesDistanceV
    row.rotation.y = (this.nodesAngle * i * Math.PI) / 180

    // sprite left
    const spriteLeft = this.createDnaNode(this.nodesMaxSize)

    spriteLeft.position.x = this.nodesDistanceH / 2

    // sprite right
    const spriteRight = this.createDnaNode(this.nodesMaxSize)

    spriteRight.position.x = -this.nodesDistanceH / 2

    // connector
    const connect = !(i % this.connectorEvery)

    if (connect) {
      const connectorNodesDistance =
        this.nodesDistanceH / this.connectorNodesCount

      for (let c = 0; c < this.connectorNodesCount; c++) {
        const spriteConnector = this.createDnaNode(this.connectorMaxSize)

        spriteConnector.position.x =
          -(this.nodesDistanceH / 2) + c * connectorNodesDistance
        row.add(spriteConnector)
      }
    }

    row.add(spriteLeft)
    row.add(spriteRight)

    return row
  }

  createDnaNode = maxSize => {
    const sprite = this.dnaNodeSprite.clone()
    const material = this.dnaNodeMaterial.clone()
    const radius = random(0.2, maxSize)
    const opacity = random(0.3, 1)

    sprite.material = material
    sprite.material.opacity = opacity
    sprite.scale.set(radius, radius, 1.0)
    sprite.position.x = -this.nodesDistanceH / 2

    return sprite
  }

  replaceRows = () => {
    this.nodesCounter++
    this.dna.remove(this.dna.children[0])
    this.dna.add(this.createDnaRow(this.nodesCounter))
  }

  animate = () => {
    requestAnimationFrame(this.animate)

    this.dna.rotation.y -= this.dnaRotationSpeed

    this.speedDelta++

    this.dna.position.y += this.nodesDistanceV / this.speedRetraction
    if (this.speedDelta % this.speedRetraction === 0) {
      this.replaceRows()
    }

    for (let i = 0; i <= this.nodesInitialCount; i++) {
      each(this.dna.children[i].children, node => {
        if (this.nodesPosition[node.id]) {
          this.animPositionK -= 0.000003
          this.nodesPosition[node.id][1].x *= this.animPositionK
          this.nodesPosition[node.id][1].y *= this.animPositionK
          this.nodesPosition[node.id][1].z *= this.animPositionK

          node.position.x =
            this.nodesPosition[node.id][0].x + this.nodesPosition[node.id][1].x
          node.position.y =
            this.nodesPosition[node.id][0].y + this.nodesPosition[node.id][1].y
          node.position.z =
            this.nodesPosition[node.id][0].z + this.nodesPosition[node.id][1].z

          if (node.material.opacity < node.material.userData) {
            node.material.opacity += this.animOpacityK
          }
        }
      })
    }

    this.renderer.render(this.scene, this.camera)

    return null
  }

  resizeHandler = () => {
    const { height, width } = this.wrapperRef.current.getBoundingClientRect()

    this.camera.aspect = width / height
    this.renderer.setSize(width, height)
    this.camera.updateProjectionMatrix()
  }

  render() {
    const { restrictUIX } = this.props

    return (
      <Wrapper>
        <Inner ref={this.wrapperRef}>{restrictUIX.noWebGL && <Plug />}</Inner>
      </Wrapper>
    )
  }
}
